import {Component} from '@angular/core';
import {PrimeNgImportsModule} from '../../../../sdk/src/prime-ng-imports';
import {IBaseCRUDComponent} from '../../../../sdk/src/components/ibase-crud/ibase-crud.component';
import {CpmCCustomerApiModel} from '../../../../gen/cpm-c-api-ts/src/models/c-common/cpmc-customer.api-model';
import {
    NewEditCustomerModalComponent,
    NewEditCustomerModalConfig,
    NewEditCustomerModalResult
} from '../../modals/new-edit-customer-modal/new-edit-customer-modal.component';
import {CpmCCustomerApiService} from '../../../../gen/cpm-c-api-ts/src/services/c-common/cpmc-customer.api-service';
import {firstValueFrom} from 'rxjs';
import {EDeleteMode} from '../../../../sdk/src/enums/delete-mode.enum';
import {ButtonGroupModule} from 'primeng/buttongroup';
import {CustomerUrlBuilderUtil} from '../../utils/customer-url-builder.util';
import {MenuItem} from 'primeng/api';

@Component({
    selector: 'app-g-customers-page',
    templateUrl: './customers-page.component.html',
    styleUrl: './customers-page.component.scss',
    imports: [PrimeNgImportsModule, ButtonGroupModule]
})
export class CustomersPageComponent extends IBaseCRUDComponent<
    CpmCCustomerApiModel,
    NewEditCustomerModalComponent,
    NewEditCustomerModalConfig,
    NewEditCustomerModalResult
> {
    protected layout: 'list' | 'grid' = 'grid';
    protected desc = '';

    // ===============================================================
    // ===============================================================

    /**
     * Constructor
     */
    constructor() {
        super();

        this.configure({
            // Model config
            modelName: 'Customer',
            getModelHumanIdentifier: (model: CpmCCustomerApiModel) => model.name ?? '<Unknown customer>',

            // Service config
            deleteMode: EDeleteMode.CONFIRM_REENFORCED,
            deleteModelService: (modelToDelete: CpmCCustomerApiModel) =>
                CpmCCustomerApiService.deleteCustomer(modelToDelete.uuid!),

            // Modal config
            modalType: NewEditCustomerModalComponent,
            getDialogServiceParameters: NewEditCustomerModalComponent.getDialogServiceParameters,
            modalExtendedConfigBuilder: (model?: CpmCCustomerApiModel) => new NewEditCustomerModalConfig(model),

            // Authorization config
            authorizationNew: CpmCCustomerApiService.endpointNew(),
            authorizationEdit: CpmCCustomerApiService.endpointEdit(this.FAKE_UUID),
            authorizationDelete: CpmCCustomerApiService.endpointDelete(this.FAKE_UUID)
        });
    }

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onReady(): Promise<void> {}
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onDestroy(): Promise<void> {}

    // ===============================================================
    // ===============================================================

    /**
     * On refresh (Error handling is done in the parent)
     */
    protected override async onRefresh(): Promise<void> {
        this.showLoading = true;
        this.loadingStr = 'Loading customers...';

        const result = await firstValueFrom(
            CpmCCustomerApiService.getCustomerList({}, CpmCCustomerApiService.dPaginatorConfig)
        );

        this.items = result.data;
        this.totalItemCount = result.total;

        this.showLoading = false;
    }

    // ===============================================================
    // ===============================================================

    protected onCustomerSelected(customer: CpmCCustomerApiModel) {
        this.globalsService.router.navigate([CustomerUrlBuilderUtil.customer(customer?.uuid)]);
    }

    // ===============================================================
    // ===============================================================

    /**
     * Init the columns (Error handling is done in the parent)
     */
    protected override async initColumns(): Promise<void> {
        // Nothing to do here
    }

    // ===============================================================
    // ===============================================================

    protected override buildDefaultContextMenu(row: CpmCCustomerApiModel): MenuItem[] {
        return [
            {
                label: 'View',
                icon: 'pi pi-eye',
                command: () => {
                    this.onEdit(row, true);
                }
            },
            {
                label: 'Edit',
                icon: 'pi pi-pencil',
                disabled: !this.isEditAllowed,
                command: () => {
                    this.onEdit(row, false);
                }
            },
            {
                label: 'Delete',
                icon: 'pi pi-trash',
                disabled: !this.isDeleteAllowed,
                command: () => {
                    this.onDelete(row);
                }
            }
        ];
    }
}
